import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PRESENCE_STATUS,
  ONLINE_TIMEOUT,
  DEFAULT_OFFLINE_TIMEOUT
} from '../utils/dashboard/constants';

function memberIdOf(member) {
  return member?.device_unique_id || member?.name || '';
}

function lastSeenMsOf(member) {
  if (!member?.last_seen) return NaN;
  const t = new Date(member.last_seen).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/**
 * 方案3 混合三态：
 * - 绿：有效 last_seen 静默未超过「暂时离线」阈值（WS/抓取有更新会立刻变绿）
 * - 橙：静默超过暂时离线、未到正式离线（前端定时重算，约 5 秒内变灯）
 * - 灰：静默超过管理后台正式离线时长
 *
 * 有效时间取「本地已确认的最新 last_seen」与本次上报的较大值，避免旧抓取回退冲掉绿灯。
 */
export default function useMemberPresenceTracker({
  members,
  tempOfflineTimeoutMs = ONLINE_TIMEOUT,
  offlineTimeoutMs = DEFAULT_OFFLINE_TIMEOUT
}) {
  const [presenceById, setPresenceById] = useState(() => ({}));
  const confirmedSeenRef = useRef(new Map());
  const presenceRef = useRef({});
  const membersRef = useRef(members);
  const tempTimeoutRef = useRef(tempOfflineTimeoutMs);
  const offlineTimeoutRef = useRef(offlineTimeoutMs);

  useEffect(() => { tempTimeoutRef.current = tempOfflineTimeoutMs; }, [tempOfflineTimeoutMs]);
  useEffect(() => { offlineTimeoutRef.current = offlineTimeoutMs; }, [offlineTimeoutMs]);
  useEffect(() => { presenceRef.current = presenceById; }, [presenceById]);
  useEffect(() => { membersRef.current = members; }, [members]);

  const recompute = useCallback((list) => {
    const now = Date.now();
    const tempMs = Math.max(5000, Number(tempTimeoutRef.current) || ONLINE_TIMEOUT);
    const offlineMs = Math.max(tempMs + 1000, Number(offlineTimeoutRef.current) || DEFAULT_OFFLINE_TIMEOUT);
    const confirmed = confirmedSeenRef.current;
    const next = {};
    let changed = false;

    (list || []).forEach((member) => {
      const id = memberIdOf(member);
      if (!id) return;
      const seen = lastSeenMsOf(member);
      if (!Number.isFinite(seen)) {
        next[id] = PRESENCE_STATUS.OFFLINE;
        return;
      }

      const prev = confirmed.get(id);
      const effective = prev == null ? seen : Math.max(prev, seen);
      if (prev == null || seen > prev) {
        confirmed.set(id, effective);
      }

      const age = now - effective;
      let status;
      if (age >= offlineMs) status = PRESENCE_STATUS.OFFLINE;
      else if (age >= tempMs) status = PRESENCE_STATUS.TEMP_OFFLINE;
      else status = PRESENCE_STATUS.ONLINE;
      next[id] = status;
    });

    const alive = new Set(Object.keys(next));
    Array.from(confirmed.keys()).forEach((id) => {
      if (!alive.has(id)) confirmed.delete(id);
    });

    const prevPresence = presenceRef.current || {};
    const keys = Object.keys(next);
    if (
      keys.length !== Object.keys(prevPresence).length
      || keys.some((k) => prevPresence[k] !== next[k])
    ) {
      changed = true;
    }

    if (changed) {
      presenceRef.current = next;
      setPresenceById(next);
    }
  }, []);

  // 推送/列表变化：有更新立刻绿；静默已超时则立刻橙/灰
  useEffect(() => {
    recompute(members);
  }, [members, recompute]);

  // 定时：接近实时推进橙/灰（不增加队员端耗电）
  useEffect(() => {
    const timer = setInterval(() => {
      recompute(membersRef.current);
    }, 5000);
    return () => clearInterval(timer);
  }, [recompute]);

  /** 设备列表抓取完成后立即按静默重算（与 members 更新互补） */
  const notifyDevicesFetched = useCallback((list) => {
    recompute(list || membersRef.current);
  }, [recompute]);

  const getPresence = useCallback((memberOrId) => {
    const id = typeof memberOrId === 'string' ? memberOrId : memberIdOf(memberOrId);
    return presenceById[id] || PRESENCE_STATUS.OFFLINE;
  }, [presenceById]);

  return { presenceById, getPresence, notifyDevicesFetched };
}
